import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, CheckCircle, GraduationCap, Building2, Cpu, ArrowRight, BookOpen, Scale, FileText, Briefcase, Shield, ChevronRight, Euro } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage } from '@/contexts/LanguageContext';

export const TrainingPrograms = () => {
  const { t, language } = useLanguage();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  const activeIndex = hoveredIndex !== null ? hoveredIndex : 0;

  const modules = language === 'fr' ? [
    {
      icon: GraduationCap,
      title: 'Formation continue obligatoire (FCO)',
      subtitle: 'Avocats salariés & libéraux · Notaires',
      description: 'Modules éligibles OPCO, FIF-PL et formation continue obligatoire des avocats (20h/an). IA appliquée au métier de professionnel du droit.',
      badge: 'FCO · OPCO ·\nFIF-PL',
      popular: true,
      duration: 'Modules de 2h à 7h',
      format: 'Webinaire ou présentiel',
      valueLabel: 'Éligible financement OPCO / FIF-PL',
      link: '/formation-continue-obligatoire-avocat-ia',
      features: [
        'Fondamentaux de l\'IA générative pour le droit',
        'Prompt engineering juridique avancé',
        'Déontologie et conformité RGPD de l\'IA',
        'Attestation de formation continue CNB',
      ],
    },
    {
      icon: Building2,
      title: 'Formation Bootcamp cabinet & direction juridique',
      subtitle: 'Cabinets d\'avocats · Directions juridiques · Études notariales',
      description: 'Programmes sur-mesure adaptés à votre organisation. Formation de vos équipes aux outils IA avec cas d\'usage concrets issus de votre pratique.',
      badge: null,
      popular: false,
      duration: 'De 1 jour à 3 mois',
      format: 'Webinaire ou présentiel, intra ou inter',
      valueLabel: 'Devis personnalisé',
      link: '/formation-ia-sur-mesure-cabinet-avocat',
      features: [
        'Audit préalable de vos besoins et workflows',
        'Cas d\'usage sur vos propres documents',
        'Formation aux outils IA adaptés à votre pratique',
        'Suivi post-formation de 30 jours',
      ],
    },
    {
      icon: Cpu,
      title: 'Solutions IA juridique sur-mesure',
      subtitle: 'Expert · Technique · Transformation',
      description: 'Conception et déploiement de solutions d\'intelligence artificielle personnalisées pour votre cabinet ou direction juridique.',
      badge: null,
      popular: false,
      duration: '2 à 6 mois',
      format: 'Accompagnement dédié',
      valueLabel: 'Sur devis · ROI garanti',
      link: '/solution-ia-juridique-sur-mesure',
      features: [
        'Audit technique de vos processus existants',
        'Conception d\'agents IA spécialisés',
        'Intégration avec vos outils (GED, LegalTech)',
        'Formation des équipes et maintenance',
      ],
    },
  ] : [
    {
      icon: GraduationCap,
      title: 'Mandatory continuing legal education (CLE)',
      subtitle: 'Employed & Independent Lawyers · Notaries',
      description: 'Modules eligible for OPCO, FIF-PL and mandatory continuing education (20h/year). AI applied to legal practice.',
      badge: 'CLE · OPCO ·\nFIF-PL',
      popular: true,
      duration: '2h to 7h modules',
      format: 'Webinar or on-site',
      valueLabel: 'Eligible for OPCO / FIF-PL funding',
      link: '/mandatory-cle-ai-training-lawyers',
      features: [
        'Generative AI fundamentals for law',
        'Advanced legal prompt engineering',
        'Ethics and GDPR compliance for AI',
        'CLE certification issued',
      ],
    },
    {
      icon: Building2,
      title: 'Custom firm & legal department training',
      subtitle: 'Law Firms · Legal Departments · Notary Offices',
      description: 'Tailored programs for your organization. Train your teams on AI tools with concrete use cases from your practice.',
      badge: null,
      popular: false,
      duration: '1 day to 3 months',
      format: 'Webinar or on-site',
      valueLabel: 'Custom quote',
      link: '/custom-ai-training-law-firm',
      features: [
        'Pre-audit of your needs and workflows',
        'Use cases on your own documents',
        'Training on AI tools suited to your practice',
        '30-day post-training support',
      ],
    },
    {
      icon: Cpu,
      title: 'Custom legal AI solutions',
      subtitle: 'Expert · Technical · Transformation',
      description: 'Design and deployment of custom AI solutions for your firm or legal department. From concept to production.',
      badge: null,
      popular: false,
      duration: '2 to 6 months',
      format: 'Dedicated consulting',
      valueLabel: 'Custom quote · Guaranteed ROI',
      link: '/custom-legal-ai-solutions',
      features: [
        'Technical audit of your existing processes',
        'Design of specialized AI agents',
        'Integration with your tools (DMS, LegalTech)',
        'Team training and maintenance',
      ],
    },
  ];

  const practiceAreas = language === 'fr' ? [
    { name: 'Private Equity & M&A', cases: 'Due diligence automatisée, analyse de datarooms, détection de clauses de changement de contrôle' },
    { name: 'Contentieux & Arbitrage', cases: 'Recherche jurisprudentielle assistée, analyse prédictive, rédaction de conclusions' },
    { name: 'Droit social', cases: 'Analyse de conventions collectives, comparaison de contrats de travail, veille réglementaire' },
    { name: 'Corporate & Droit des sociétés', cases: 'Rédaction de PV d\'AG, comparaison de statuts, conformité KYC/AML' },
    { name: 'Droit fiscal', cases: 'Analyse de rescrits, veille fiscale automatisée, optimisation de mémos' },
    { name: 'Droit public', cases: 'Analyse d\'appels d\'offres, conformité marchés publics, rédaction de mémoires' },
    { name: 'Droit immobilier', cases: 'Analyse de baux commerciaux, due diligence immobilière, vérification d\'urbanisme' },
    { name: 'Droit de la santé', cases: 'Conformité données de santé, analyse de protocoles, veille ANSM/HAS' },
    { name: 'Droit de l\'urbanisme', cases: 'Analyse de PLU, vérification de permis de construire, contentieux administratif' },
    { name: 'Regulatory & Compliance', cases: 'Veille réglementaire automatisée, cartographie des obligations, analyse d\'impact' },
    { name: 'Droit pénal', cases: 'Recherche jurisprudentielle pénale, analyse de dossiers, chronologie automatisée' },
  ] : [
    { name: 'Private Equity & M&A', cases: 'Automated due diligence, dataroom analysis, change of control clause detection' },
    { name: 'Litigation & Arbitration', cases: 'AI-assisted case law research, predictive analysis, brief drafting' },
    { name: 'Employment law', cases: 'Collective agreement analysis, contract comparison, automated regulatory watch' },
    { name: 'Corporate & Company law', cases: 'Board minutes drafting, articles comparison, KYC/AML compliance' },
    { name: 'Tax law', cases: 'Tax ruling analysis, automated tax watch, memo optimization' },
    { name: 'Public law', cases: 'Tender analysis, public procurement compliance, memorial drafting' },
    { name: 'Real estate', cases: 'Commercial lease analysis, real estate due diligence, zoning verification' },
    { name: 'Healthcare law', cases: 'Health data compliance, protocol analysis, regulatory watch' },
    { name: 'Urban planning law', cases: 'Local plan analysis, building permit verification, administrative litigation' },
    { name: 'Regulatory & Compliance', cases: 'Automated regulatory watch, obligation mapping, impact analysis' },
    { name: 'Criminal law', cases: 'Criminal case law research, case file analysis, automated fact chronology' },
  ];

  const useCases = language === 'fr' ? [
    { name: 'Drafting & rédaction', desc: 'Génération de premières versions de contrats, clauses types, courriers, conclusions' },
    { name: 'Relecture & comparaison', desc: 'Identification automatique d\'écarts entre versions, détection de clauses inhabituelles' },
    { name: 'Analyse de risques', desc: 'Scoring automatisé du niveau de risque contractuel, alertes sur clauses sensibles' },
    { name: 'Renouvellements de contrats', desc: 'Analyse automatisée des échéances, alertes de renouvellement, synthèse des obligations' },
    { name: 'Recherche juridique', desc: 'Recherche jurisprudentielle augmentée, synthèse de doctrine, veille automatisée' },
    { name: 'Due diligence', desc: 'Extraction automatisée d\'informations clés, red flags, rapports de synthèse' },
  ] : [
    { name: 'Drafting & writing', desc: 'First-draft generation for contracts, standard clauses, letters, briefs' },
    { name: 'Review & comparison', desc: 'Automatic identification of discrepancies, unusual clause detection' },
    { name: 'Risk analysis', desc: 'Automated contractual risk scoring, alerts on sensitive clauses' },
    { name: 'Contract renewals', desc: 'Automated deadline analysis, renewal alerts, obligation summaries' },
    { name: 'Legal research', desc: 'AI-augmented case law research, doctrine synthesis, automated monitoring' },
    { name: 'Due diligence', desc: 'Automated extraction of key information, red flags, summary reports' },
  ];

  const solutions = language === 'fr' ? [
    { name: 'Agent IA de recherche juridique', desc: 'Chatbot interne connecté à vos bases documentaires et jurisprudentielles' },
    { name: 'Automatisation du drafting', desc: 'Génération automatisée de contrats types à partir de vos modèles et clauses' },
    { name: 'Analyse documentaire IA', desc: 'Pipeline d\'extraction et de classification automatique de documents juridiques' },
    { name: 'Dashboard de veille réglementaire', desc: 'Monitoring automatisé des évolutions législatives sur vos matières' },
  ] : [
    { name: 'Legal research AI agent', desc: 'Internal chatbot connected to your document and case law databases' },
    { name: 'Drafting automation', desc: 'Automated contract generation from your templates and clause libraries' },
    { name: 'AI document analysis', desc: 'Automated extraction and classification pipeline for legal documents' },
    { name: 'Regulatory watch dashboard', desc: 'Automated monitoring of legislative changes in your practice areas' },
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {language === 'fr' ? 'Nos offres de formation en IA pour professionnels du droit' : 'Our AI training programs for legal professionals'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {language === 'fr'
              ? 'De la formation continue obligatoire aux solutions IA sur-mesure, nous accompagnons avocats, juristes et notaires à chaque étape.'
              : 'From mandatory CLE to custom AI solutions, we support lawyers, counsel and notaries at every stage.'}
          </p>
          <div className="mt-6 relative inline-block">
            <Button asChild size="lg" className="bg-accent/80 text-accent-foreground hover:bg-accent">
              <Link to={language === 'fr' ? '/formation-ia-juridique-eligible-financement' : '/legal-ai-training-eligible-funding'}>
                <Euro className="w-4 h-4" />
                {language === 'fr' ? 'Formations éligibles financement (OPCO · FIF-PL · CPF)' : 'Funded training programs (OPCO · FIF-PL · CPF)'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Badge className="absolute -top-3 -right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md">
              {language === 'fr' ? 'Bientôt disponible' : 'Soon'}
            </Badge>
          </div>
        </div>

        {/* Module cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {modules.map((mod, index) => {
            const isActive = index === activeIndex;
            const Icon = mod.icon;
            return (
              <Card
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`flex flex-col border-border relative transition-all duration-300 cursor-pointer ${isActive ? 'bg-white ring-2 ring-primary shadow-xl scale-[1.02]' : 'bg-white shadow-sm hover:shadow-md'}`}
              >
                {mod.badge && isActive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm whitespace-pre-line text-center">
                      {mod.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className={`text-center ${mod.badge && isActive ? 'pt-8' : ''}`}>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 mx-auto">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl text-foreground">{mod.title}</CardTitle>
                  <p className="text-sm text-primary font-medium">{mod.subtitle}</p>
                  <CardDescription className="text-muted-foreground mt-2">
                    {mod.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="flex flex-col space-y-2 mb-5 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span>{mod.duration}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span>{mod.format}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {mod.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t border-border pt-5 space-y-3">
                    <div className="text-base font-semibold text-primary mb-4">
                      {mod.valueLabel}
                    </div>
                    {index === 0 ? (
                      <div className="relative">
                        <Button asChild className="w-full opacity-75 hover:opacity-100">
                          <Link to={mod.link} className="flex items-center justify-center">
                            {language === 'fr' ? 'Découvrir la formation' : 'Discover the program'}
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Link>
                        </Button>
                        <Badge className="absolute -top-3 -right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md">
                          {language === 'fr' ? 'Bientôt disponible' : 'Soon'}
                        </Badge>
                      </div>
                    ) : (
                      <Button asChild className={`w-full ${isActive ? 'bg-primary hover:bg-primary/90' : 'bg-primary/80 hover:bg-primary'}`}>
                        <Link to={mod.link} className="flex items-center justify-center">
                          {language === 'fr' ? 'Découvrir la formation' : 'Discover the program'}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    )}
                    <Button asChild variant="outline" className="w-full">
                      <Link to={mod.link}>
                        {language === 'fr' ? 'Demander des informations' : 'Request information'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Accordion sections */}
        <Accordion type="single" collapsible className="space-y-4">
          {/* FCO - Practice Areas */}
          <AccordionItem value="practice-areas" className="border border-border rounded-xl px-6 bg-card">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">
                  {language === 'fr' ? 'Formation continue par matière juridique' : 'CLE training by practice area'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground mb-6 max-w-3xl">
                {language === 'fr'
                  ? 'Chaque module est conçu pour une matière juridique spécifique, avec des cas d\'usage concrets et des exercices pratiques sur les outils IA les plus pertinents.'
                  : 'Each module is designed for a specific practice area, with concrete use cases and hands-on exercises on the most relevant AI tools.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {practiceAreas.map((area, i) => (
                  <div key={i} className="p-4 rounded-xl border border-border bg-secondary/30 hover:shadow-md hover:border-primary/20 transition-all">
                    <h4 className="text-sm font-semibold text-foreground mb-2">{area.name}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{area.cases}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button asChild variant="outline" size="sm">
                  <Link to={language === 'fr' ? '/formation-continue-obligatoire-avocat-ia' : '/mandatory-cle-ai-training-lawyers'} className="flex items-center">
                    {language === 'fr' ? 'Voir le détail de la formation FCO' : 'View CLE training details'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Ad Hoc - Use Cases */}
          <AccordionItem value="use-cases" className="border border-border rounded-xl px-6 bg-card">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">
                  {language === 'fr' ? 'Cas d\'usage : formation ad hoc' : 'Use cases: custom training'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground mb-6 max-w-3xl">
                {language === 'fr'
                  ? 'Nos formations ad hoc s\'appuient sur vos propres documents et workflows. Voici les cas d\'usage les plus demandés par nos clients :'
                  : 'Our custom training uses your own documents and workflows. Here are the most requested use cases:'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {useCases.map((uc, i) => (
                  <div key={i} className="p-5 rounded-xl border border-border bg-secondary/30 hover:shadow-md hover:border-primary/20 transition-all">
                    <h4 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      {uc.name}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button asChild variant="outline" size="sm">
                  <Link to={language === 'fr' ? '/formation-ia-sur-mesure-cabinet-avocat' : '/custom-ai-training-law-firm'} className="flex items-center">
                    {language === 'fr' ? 'Voir le détail de la formation ad hoc' : 'View custom training details'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Solutions - Technical */}
          <AccordionItem value="solutions" className="border border-border rounded-xl px-6 bg-card">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Cpu className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">
                  {language === 'fr' ? 'Solutions IA sur-mesure' : 'Custom AI solutions'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground mb-6 max-w-3xl">
                {language === 'fr'
                  ? 'Au-delà de la formation, nous concevons et déployons des solutions d\'IA juridique personnalisées pour transformer durablement votre pratique.'
                  : 'Beyond training, we design and deploy custom legal AI solutions to durably transform your practice.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {solutions.map((sol, i) => (
                  <div key={i} className="p-5 rounded-xl border border-border bg-gradient-to-br from-primary/5 to-accent/5 hover:shadow-md hover:border-primary/20 transition-all">
                    <h4 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-primary" />
                      {sol.name}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{sol.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button asChild variant="outline" size="sm">
                  <Link to={language === 'fr' ? '/solution-ia-juridique-sur-mesure' : '/custom-legal-ai-solutions'} className="flex items-center">
                    {language === 'fr' ? 'Voir le détail des solutions' : 'View solutions details'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
};
